import { createContext, useContext } from "react";
import {
  isCMSNode,
  isRichText,
  type CMSNode,
  type PageContext,
  type SiteSettings,
} from "../types.ts";
import RichText from "./RichText.tsx";
import { registry } from "./registry.tsx";

export const PageContextValue = createContext<PageContext>({
  space: "core",
  pageId: "",
  isHome: true,
  debug: false,
});

export const SiteContextValue = createContext<SiteSettings>({
  header: { navLinks: [] },
  footer: {},
  social: { links: [] },
  seo: { siteName: "" },
});

export interface CMSComponentProps<T extends CMSNode = CMSNode> {
  node: T;
}

/** Resolves a page model node to its registered renderer. */
export function CMSComponent({ node }: { node: unknown }) {
  if (!isCMSNode(node)) return null;
  const Component = registry[node.componentType];
  if (Component === null) return null; // config-only node, consumed by its parent
  if (Component) return <Component node={node} />;
  return <UnknownComponent node={node} />;
}

export function CMSComponents({ nodes }: { nodes: unknown }) {
  if (!Array.isArray(nodes)) return null;
  return (
    <>
      {nodes.map((node, index) => (
        <CMSComponent key={isCMSNode(node) ? (node.id ?? index) : index} node={node} />
      ))}
    </>
  );
}

/**
 * No renderer registered: still render nested content so the page degrades
 * gracefully, and outline it in dev so the gap is visible.
 */
function UnknownComponent({ node }: CMSComponentProps) {
  const { debug } = useContext(PageContextValue);
  const children = Object.values(node).map((value, index) => {
    if (isRichText(value)) return <RichText key={index} document={value} />;
    if (isCMSNode(value)) return <CMSComponent key={index} node={value} />;
    if (Array.isArray(value)) return <CMSComponents key={index} nodes={value} />;
    return null;
  });

  if (!debug) return <div>{children}</div>;
  return (
    <div className="cms-unknown" data-component-type={node.componentType}>
      <span className="cms-unknown__label">{node.componentType}</span>
      {children}
    </div>
  );
}
